import { RestApiItemDto, RestApiItemBehaviourAgeLimitDto } from '@order-mage/integration-sdk';
import { Country } from '@/shared/model/Country';

/**
 * Get the applicable age restriction for an item based on country code.
 * Returns the first matching restriction for the country, or the first restriction without a country code.
 */
export const getAgeRestriction = (
    item: RestApiItemDto | null | undefined,
    countryCode?: Country,
): RestApiItemBehaviourAgeLimitDto | null => {
    if (!item?.ageLimitBehaviours || item.ageLimitBehaviours.length === 0) {
        return null;
    }

    // First try to find a restriction matching the country code
    if (countryCode) {
        const countrySpecific = item.ageLimitBehaviours.find(
            (restriction) => restriction.countryCode === countryCode,
        );
        if (countrySpecific) {
            return countrySpecific;
        }
    }

    // Fall back to first restriction without country code, or just first restriction
    return (
        item.ageLimitBehaviours.find((restriction) => !restriction.countryCode) || item.ageLimitBehaviours[0]
    );
};

/**
 * Calculate age in years from a birthdate string (YYYY-MM-DD format).
 */
export const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    // Adjust age if birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
    }

    return age;
};

/**
 * Check if a user with the given birthdate meets the minimum age requirement.
 */
export const isAgeEligible = (birthDate: string | null | undefined, minimumAge: number): boolean => {
    if (!birthDate) {
        return false;
    }

    try {
        const age = calculateAge(birthDate);
        return age >= minimumAge;
    } catch {
        return false;
    }
};
